import { useState } from 'react';
import { Button, Input } from '../../design-system';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { ApiError } from '../../lib/apiClient';
import { useActiveCars } from '../fleet/fleet.api';
import { usePassen } from '../passen/passen.api';
import { useLinkAuto, useLinkPas } from './personen.api';

/** Popup: link a car to the person (choose car + optional start date). */
export function KoppelAutoModal({
  persoonId,
  reedsGekoppeld,
  onClose,
}: {
  persoonId: string;
  reedsGekoppeld: string[];
  onClose: () => void;
}) {
  const cars = useActiveCars();
  const link = useLinkAuto(persoonId);
  const [kenteken, setKenteken] = useState('');
  const [van, setVan] = useState('');
  const [error, setError] = useState('');

  const opties = (cars.data ?? []).filter((c) => !reedsGekoppeld.includes(c.kenteken));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!kenteken) return;
    setError('');
    try {
      await link.mutateAsync({ kenteken, ...(van ? { van } : {}) });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Koppelen mislukt');
    }
  }

  return (
    <Modal open onClose={onClose} title="Auto koppelen">
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Dropdown
          label="Auto"
          block
          placeholder="Kies een auto…"
          value={kenteken}
          onChange={setKenteken}
          options={opties.map((c) => ({
            value: c.kenteken,
            label: `${c.kenteken} · ${c.merk} ${c.model}`,
          }))}
        />
        <Input
          label="Ingangsdatum (optioneel)"
          type="date"
          value={van}
          onChange={(e) => setVan(e.target.value)}
          hint="Leeg = vandaag."
        />
        {error && <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" type="button" onClick={onClose}>
            Annuleren
          </Button>
          <Button variant="primary" type="submit" disabled={link.isPending || !kenteken}>
            Koppelen
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Popup: link a pass to the person (choose pass + optional start date). */
export function KoppelPasModal({ persoonId, onClose }: { persoonId: string; onClose: () => void }) {
  const passen = usePassen();
  const link = useLinkPas(persoonId);
  const [cardId, setCardId] = useState('');
  const [van, setVan] = useState('');
  const [error, setError] = useState('');

  // Passes not already held by this person; fictitious/unlinked first.
  const opties = (passen.data ?? [])
    .filter((p) => p.persoonId !== persoonId)
    .sort((a, b) => Number(!!b.fictief) - Number(!!a.fictief));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!cardId) return;
    setError('');
    try {
      await link.mutateAsync({ cardId, ...(van ? { van } : {}) });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Koppelen mislukt');
    }
  }

  return (
    <Modal open onClose={onClose} title="Pas koppelen">
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Dropdown
          label="Pas"
          block
          placeholder="Kies een pas…"
          value={cardId}
          onChange={setCardId}
          options={opties.map((p) => ({
            value: p.id,
            label: `${p.pasnummer}${p.fictief ? ' — fictief' : p.persoonNaam ? ` — nu: ${p.persoonNaam}` : ' — ongekoppeld'}`,
          }))}
        />
        <Input
          label="Ingangsdatum (optioneel)"
          type="date"
          value={van}
          onChange={(e) => setVan(e.target.value)}
          hint="Leeg = eerste koppeling neemt de hele historie mee, wissel gebruikt vandaag."
        />
        {error && <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" type="button" onClick={onClose}>
            Annuleren
          </Button>
          <Button variant="primary" type="submit" disabled={link.isPending || !cardId}>
            Koppelen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
